import { FacebookApi } from '@/api/FacebookApi';
import { GoogleMyBusinessApi } from '@/api/GoogleMyBusinessApi';
import { InstagramApi } from '@/api/InstagramApi';
import { LinkedInApi } from '@/api/LinkedInApi';
import { describePostPublishError } from '@/api/PostApi';
import { SOCIAL_PLATFORMS_METADATA } from '@/constants';
import { SocialFeedItem, SocialFeedPlatform } from '@/types/socialFeed';
import { CrossPostDraft } from '@/utils/crossPostDraft';
import { Bot, PublishPost, SupportedPlatform } from 'ignite360-core';
import { extractApiErrorMessage, getDeletePostErrorNotification } from '@/utilities';

export const CROSS_POST_PLATFORMS: SupportedPlatform[] = [
  SupportedPlatform.Instagram,
  SupportedPlatform.FacebookMessenger,
  SupportedPlatform.LinkedIn,
  SupportedPlatform.GoogleBusiness,
  SupportedPlatform.AppleBusinessConnect,
];

export function getCrossPostPlatformOptions(bot: Bot, sourcePlatform: SocialFeedPlatform) {
  return SOCIAL_PLATFORMS_METADATA.filter(
    (meta) =>
      CROSS_POST_PLATFORMS.includes(meta.value as SupportedPlatform) &&
      meta.value !== sourcePlatform &&
      !!meta.isConnected?.(bot) &&
      !meta.isDisabled?.(bot),
  ).map((meta) => ({
    value: meta.value as SupportedPlatform,
    label: meta.label,
    image: meta.image || '',
  }));
}

export function buildPublishDataFromFeedItem(
  item: SocialFeedItem,
  selectedPlatforms: SupportedPlatform[],
): PublishPost & { selectedPlatforms: SupportedPlatform[] } {
  const isTextOnlySource =
    item.platform === SupportedPlatform.FacebookMessenger ||
    item.platform === SupportedPlatform.GoogleBusiness;
  const title = isTextOnlySource ? '' : (item.title || '').trim();
  const content = (item.content || item.title || '').trim();
  return {
    title,
    content,
    selectedPlatforms: selectedPlatforms.slice(),
  };
}

export async function resolveCrossPostMedia(
  botId: string,
  item: SocialFeedItem,
): Promise<{
  images: { horizontal: File | null; square: File | null };
  video: File | null;
}> {
  const empty = {
    images: { horizontal: null, square: null },
    video: null,
  };
  if (item.platform === SupportedPlatform.Instagram) {
    const { file, kind } = await InstagramApi.downloadMediaFile(botId, item.id);
    const treatAsVideo = kind === 'video' || String(item.mediaType || '').toUpperCase() === 'VIDEO';
    if (treatAsVideo) {
      return { images: { horizontal: null, square: null }, video: file };
    }
    return { images: { horizontal: file, square: file }, video: null };
  }
  if (item.platform === SupportedPlatform.GoogleBusiness) {
    if (!item.thumbnailUrl && String(item.mediaType || '').toUpperCase() === 'TEXT') {
      return empty;
    }
    const { file, kind } = await GoogleMyBusinessApi.downloadMediaFile(botId, item.id);
    if (kind === 'video') {
      return { images: { horizontal: null, square: null }, video: file };
    }
    return { images: { horizontal: file, square: file }, video: null };
  }
  if (item.platform === SupportedPlatform.FacebookMessenger) {
    if (!item.thumbnailUrl && String(item.mediaType || '').toUpperCase() === 'TEXT') {
      return empty;
    }
    const { file, kind } = await FacebookApi.downloadMediaFile(botId, item.id);
    if (kind === 'video') {
      return { images: { horizontal: null, square: null }, video: file };
    }
    return { images: { horizontal: file, square: file }, video: null };
  }
  if (item.platform === SupportedPlatform.LinkedIn) {
    if (!item.thumbnailUrl && String(item.mediaType || '').toUpperCase() === 'TEXT') {
      return empty;
    }
    const { file, kind } = await LinkedInApi.downloadMediaFile(botId, item.id);
    const treatAsVideo = kind === 'video' || String(item.mediaType || '').toUpperCase() === 'VIDEO';
    if (treatAsVideo) {
      return { images: { horizontal: null, square: null }, video: file };
    }
    return { images: { horizontal: file, square: file }, video: null };
  }
  return empty;
}

export async function deleteSocialFeedPost(botId: string, item: SocialFeedItem): Promise<void> {
  if (item.platform === SupportedPlatform.Instagram) {
    await InstagramApi.deleteMedia(botId, item.id);
    return;
  }
  if (item.platform === SupportedPlatform.LinkedIn) {
    await LinkedInApi.deletePost(botId, item.id);
    return;
  }
  if (item.platform === SupportedPlatform.GoogleBusiness) {
    await GoogleMyBusinessApi.deletePost(botId, item.id);
    return;
  }
  await FacebookApi.deletePost(botId, item.id);
}

export async function buildCrossPostDraft(
  botId: string,
  item: SocialFeedItem,
  targetPlatforms: SupportedPlatform[],
): Promise<CrossPostDraft> {
  const data = buildPublishDataFromFeedItem(item, targetPlatforms);
  const media = await resolveCrossPostMedia(botId, item);
  return {
    botId,
    title: data.title,
    content: data.content,
    selectedPlatforms: data.selectedPlatforms,
    images: media.images,
    video: media.video,
  };
}

export { describePostPublishError, extractApiErrorMessage, getDeletePostErrorNotification };
